import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit, CreditCard, TrendingUp, TrendingDown, Save, X, Building2 } from "lucide-react";

import { API_URL } from "../../config";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";

/**
 * Settings tab to manage finance categories (income/expense) and payment methods.
 * Used inside Configurações for finance configuration.
 */
export const FinanceSettings = () => {
  const [incomeCats, setIncomeCats] = useState([]);
  const [expenseCats, setExpenseCats] = useState([]);
  const [methods, setMethods] = useState([]);
  const [payees, setPayees] = useState([]);
  const [newIncomeName, setNewIncomeName] = useState("");
  const [newExpenseName, setNewExpenseName] = useState("");
  const [newMethodName, setNewMethodName] = useState("");
  const [newPayeeName, setNewPayeeName] = useState("");
  const [newPayeeKind, setNewPayeeKind] = useState("expense");
  const [newPayeeTaxId, setNewPayeeTaxId] = useState("");

  const fetchAll = async () => {
    try {
      const [iRes, eRes, mRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/finance/categories?kind=income`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/finance/categories?kind=expense`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/finance/payment-methods`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/finance/payees`, { withCredentials: true }),
      ]);
      setIncomeCats(iRes.data || []);
      setExpenseCats(eRes.data || []);
      setMethods(mRes.data || []);
      setPayees(pRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro a carregar configuração financeira");
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const addCategory = async (kind, name, reset) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return;
    try {
      await axios.post(`${API_URL}/api/admin/finance/categories`, { name: trimmed, kind }, { withCredentials: true });
      reset("");
      toast.success("Categoria criada");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const updateCategory = async (id, patch) => {
    try {
      await axios.put(`${API_URL}/api/admin/finance/categories/${id}`, patch, { withCredentials: true });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Eliminar esta categoria?")) return;
    try {
      await axios.delete(`${API_URL}/api/admin/finance/categories/${id}`, { withCredentials: true });
      toast.success("Categoria eliminada");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const addMethod = async () => {
    const trimmed = newMethodName.trim();
    if (!trimmed) return;
    try {
      await axios.post(`${API_URL}/api/admin/finance/payment-methods`, { name: trimmed }, { withCredentials: true });
      setNewMethodName("");
      toast.success("Método criado");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const updateMethod = async (id, patch) => {
    try {
      await axios.put(`${API_URL}/api/admin/finance/payment-methods/${id}`, patch, { withCredentials: true });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const deleteMethod = async (id) => {
    if (!window.confirm("Eliminar este método?")) return;
    try {
      await axios.delete(`${API_URL}/api/admin/finance/payment-methods/${id}`, { withCredentials: true });
      toast.success("Método eliminado");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const addPayee = async () => {
    const trimmed = newPayeeName.trim();
    if (!trimmed) return;
    try {
      await axios.post(`${API_URL}/api/admin/finance/payees`, {
        name: trimmed,
        kind: newPayeeKind,
        tax_id: newPayeeTaxId.trim() || null,
      }, { withCredentials: true });
      setNewPayeeName(""); setNewPayeeTaxId(""); setNewPayeeKind("expense");
      toast.success("Entidade criada");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const updatePayee = async (id, patch) => {
    try {
      await axios.put(`${API_URL}/api/admin/finance/payees/${id}`, patch, { withCredentials: true });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const deletePayee = async (id) => {
    if (!window.confirm("Eliminar esta entidade?")) return;
    try {
      await axios.delete(`${API_URL}/api/admin/finance/payees/${id}`, { withCredentials: true });
      toast.success("Entidade eliminada");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const CategoryList = ({ items, icon: Icon, color, kind, value, onChange, addFn }) => (
    <div className="bg-zinc-900 rounded-xl border border-white/10 p-4">
      <h3 className="text-white font-semibold flex items-center gap-2 mb-3">
        <Icon className={`w-5 h-5 ${color}`} />
        Tipos de {kind === "income" ? "Receita" : "Despesa"}
      </h3>
      <div className="flex gap-2 mb-3">
        <Input
          value={value} onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addFn()}
          placeholder={kind === "income" ? "Ex: Bilhetes, Doação..." : "Ex: Catering, Aluguer..."}
          className="input-dark"
          data-testid={`finance-cat-${kind}-input`}
        />
        <Button onClick={addFn} className="btn-primary shrink-0" data-testid={`finance-cat-${kind}-add`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="space-y-2">
        {items.length === 0 && <p className="text-zinc-500 text-sm">Sem categorias</p>}
        {items.map((c) => (
          <CategoryRow key={c.id} cat={c} onUpdate={updateCategory} onDelete={deleteCategory} />
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="finance-settings">
      <div>
        <h2 className="text-xl font-bold text-white mb-1">Configuração Financeira</h2>
        <p className="text-zinc-500 text-sm">Gerir tipos de receita, tipos de despesa e métodos de pagamento.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <CategoryList
          items={incomeCats} icon={TrendingUp} color="text-emerald-400" kind="income"
          value={newIncomeName} onChange={setNewIncomeName}
          addFn={() => addCategory("income", newIncomeName, setNewIncomeName)}
        />
        <CategoryList
          items={expenseCats} icon={TrendingDown} color="text-red-400" kind="expense"
          value={newExpenseName} onChange={setNewExpenseName}
          addFn={() => addCategory("expense", newExpenseName, setNewExpenseName)}
        />
      </div>

      <div className="bg-zinc-900 rounded-xl border border-white/10 p-4">
        <h3 className="text-white font-semibold flex items-center gap-2 mb-3">
          <CreditCard className="w-5 h-5 text-violet-300" />
          Métodos de Pagamento
        </h3>
        <div className="flex gap-2 mb-3">
          <Input
            value={newMethodName} onChange={(e) => setNewMethodName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addMethod()}
            placeholder="Ex: Numerário, Transferência, MB Way..."
            className="input-dark"
            data-testid="finance-method-input"
          />
          <Button onClick={addMethod} className="btn-primary shrink-0" data-testid="finance-method-add">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <div className="space-y-2">
          {methods.length === 0 && <p className="text-zinc-500 text-sm">Sem métodos</p>}
          {methods.map((m) => (
            <MethodRow key={m.id} item={m} onUpdate={updateMethod} onDelete={deleteMethod} />
          ))}
        </div>
      </div>

      <div className="bg-zinc-900 rounded-xl border border-white/10 p-4">
        <h3 className="text-white font-semibold flex items-center gap-2 mb-1">
          <Building2 className="w-5 h-5 text-sky-300" />
          Lista de Entidades
        </h3>
        <p className="text-zinc-500 text-xs mb-3">Entidades usadas no campo &ldquo;Pago a Entidade&rdquo; / &ldquo;Recebido de&rdquo; ao adicionar uma despesa/receita — aparecem como sugestão automática.</p>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_120px_50px] gap-2 mb-3">
          <Input
            value={newPayeeName} onChange={(e) => setNewPayeeName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addPayee()}
            placeholder="Nome (ex: EDP, Fornecedor X)"
            className="input-dark"
            data-testid="finance-payee-input"
          />
          <Input
            value={newPayeeTaxId} onChange={(e) => setNewPayeeTaxId(e.target.value)}
            placeholder="NIF (opcional)"
            className="input-dark"
            data-testid="finance-payee-taxid"
          />
          <select
            value={newPayeeKind}
            onChange={(e) => setNewPayeeKind(e.target.value)}
            className="bg-zinc-800 border border-white/10 rounded-md text-white px-2 h-10 text-sm"
            data-testid="finance-payee-kind"
          >
            <option value="expense">Despesa</option>
            <option value="income">Receita</option>
            <option value="both">Ambos</option>
          </select>
          <Button onClick={addPayee} className="btn-primary shrink-0" data-testid="finance-payee-add">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <div className="space-y-2">
          {payees.length === 0 && <p className="text-zinc-500 text-sm">Sem entidades — adiciona a primeira acima.</p>}
          {payees.map((p) => (
            <PayeeRow key={p.id} item={p} onUpdate={updatePayee} onDelete={deletePayee} />
          ))}
        </div>
      </div>
    </div>
  );
};

const CategoryRow = ({ cat, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(cat.name);
  const save = () => { if (name.trim() && name !== cat.name) onUpdate(cat.id, { name: name.trim() }); setEditing(false); };
  return (
    <div className="flex items-center gap-2 bg-white/5 rounded px-3 py-2">
      {editing ? (
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} className="input-dark h-8" autoFocus />
      ) : (
        <span className={`flex-1 text-sm ${cat.active === false ? "text-zinc-500 line-through" : "text-white"}`}>{cat.name}</span>
      )}
      <Switch checked={cat.active !== false} onCheckedChange={(c) => onUpdate(cat.id, { active: c })} className="data-[state=checked]:bg-emerald-500" />
      {editing ? (
        <>
          <Button size="sm" variant="ghost" onClick={save} className="text-emerald-400"><Save className="w-4 h-4" /></Button>
          <Button size="sm" variant="ghost" onClick={() => { setName(cat.name); setEditing(false); }} className="text-zinc-400"><X className="w-4 h-4" /></Button>
        </>
      ) : (
        <>
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)} className="text-zinc-300"><Edit className="w-4 h-4" /></Button>
          <Button size="sm" variant="ghost" onClick={() => onDelete(cat.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
        </>
      )}
    </div>
  );
};

const MethodRow = ({ item, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.name);
  const save = () => { if (name.trim() && name !== item.name) onUpdate(item.id, { name: name.trim() }); setEditing(false); };
  return (
    <div className="flex items-center gap-2 bg-white/5 rounded px-3 py-2">
      {editing ? (
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} className="input-dark h-8" autoFocus />
      ) : (
        <span className={`flex-1 text-sm ${item.active === false ? "text-zinc-500 line-through" : "text-white"}`}>{item.name}</span>
      )}
      <Switch checked={item.active !== false} onCheckedChange={(c) => onUpdate(item.id, { active: c })} className="data-[state=checked]:bg-emerald-500" />
      {editing ? (
        <>
          <Button size="sm" variant="ghost" onClick={save} className="text-emerald-400"><Save className="w-4 h-4" /></Button>
          <Button size="sm" variant="ghost" onClick={() => { setName(item.name); setEditing(false); }} className="text-zinc-400"><X className="w-4 h-4" /></Button>
        </>
      ) : (
        <>
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)} className="text-zinc-300"><Edit className="w-4 h-4" /></Button>
          <Button size="sm" variant="ghost" onClick={() => onDelete(item.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
        </>
      )}
    </div>
  );
};

const PayeeRow = ({ item, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.name);
  const [taxId, setTaxId] = useState(item.tax_id || "");
  const [kind, setKind] = useState(item.kind || "expense");
  const save = () => {
    const patch = {};
    if (name.trim() && name.trim() !== item.name) patch.name = name.trim();
    if ((taxId || "").trim() !== (item.tax_id || "")) patch.tax_id = taxId.trim() || null;
    if (kind !== item.kind) patch.kind = kind;
    if (Object.keys(patch).length) onUpdate(item.id, patch);
    setEditing(false);
  };
  const kindLabel = kind === "income" ? "Receita" : kind === "both" ? "Ambos" : "Despesa";
  const kindBadge = kind === "income" ? "bg-emerald-500/15 text-emerald-300"
                  : kind === "both"   ? "bg-violet-500/15 text-violet-300"
                                      : "bg-red-500/15 text-red-300";
  return (
    <div className="flex items-center gap-2 bg-white/5 rounded px-3 py-2 flex-wrap">
      {editing ? (
        <>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="input-dark h-8 flex-1 min-w-[140px]" autoFocus />
          <Input value={taxId} onChange={(e) => setTaxId(e.target.value)} placeholder="NIF" className="input-dark h-8 w-32" />
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="bg-zinc-800 border border-white/10 rounded-md text-white px-2 h-8 text-xs">
            <option value="expense">Despesa</option>
            <option value="income">Receita</option>
            <option value="both">Ambos</option>
          </select>
        </>
      ) : (
        <>
          <span className={`flex-1 text-sm ${item.active === false ? "text-zinc-500 line-through" : "text-white"}`}>{item.name}</span>
          {item.tax_id && <span className="text-[10px] text-zinc-500 font-mono">NIF {item.tax_id}</span>}
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${kindBadge}`}>{kindLabel}</span>
        </>
      )}
      <Switch checked={item.active !== false} onCheckedChange={(c) => onUpdate(item.id, { active: c })} className="data-[state=checked]:bg-emerald-500" />
      {editing ? (
        <>
          <Button size="sm" variant="ghost" onClick={save} className="text-emerald-400"><Save className="w-4 h-4" /></Button>
          <Button size="sm" variant="ghost" onClick={() => { setName(item.name); setTaxId(item.tax_id || ""); setKind(item.kind); setEditing(false); }} className="text-zinc-400"><X className="w-4 h-4" /></Button>
        </>
      ) : (
        <>
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)} className="text-zinc-300"><Edit className="w-4 h-4" /></Button>
          <Button size="sm" variant="ghost" onClick={() => onDelete(item.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
        </>
      )}
    </div>
  );
};

